// MeetingConfirmationSender.tsx
import { SendCANmail } from "api/canmail/CANmailAPI";
import getUserInfo from "api/login/getUserInfo";
import getContact from "api/contacts/getContact";

export const sendMeetingConfirmation = async (
  meetingName: string,
  meetingUrl: string,
  meetingDate?: string,
  startTime?: string,
  duration?: number,
  recurrence?: string,
  notes?: string
) => {
  try {
    const userInfo = await getUserInfo();
    const contactId = userInfo.data.ContactId;

    if (!contactId) {
      console.error('[sendMeetingConfirmation] ContactId not found in user info:', userInfo);
      throw new Error("ContactId not found in user info");
    }

    const contactData = await getContact(contactId);
    const userEmail = contactData.WorkEmail;

    if (!userEmail) {
      console.error('[sendMeetingConfirmation] WorkEmail not found in contact data:', contactData);
      throw new Error("WorkEmail not found in contact data");
    }

    // Format meeting details for display
    const formatMeetingDetails = () => {
      let details = `<div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-radius: 6px; border-left: 4px solid #64b5f6;">`;
      details += `<h3 style="margin: 0 0 10px 0; color: #333; font-size: 16px;">Meeting Details</h3>`;
      details += `<p style="margin: 5px 0; color: #555;"><strong>Meeting Name:</strong> ${meetingName}</p>`;
      
      if (meetingDate && startTime) {
        const startDateTime = new Date(`${meetingDate}T${startTime}`);

        // Get the creator's timezone info
        const creatorTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Get timezone abbreviation (e.g., PDT, MST, EST)
        const timezoneShort = new Intl.DateTimeFormat('en-US', {
          timeZone: creatorTimezone,
          timeZoneName: 'short'
        }).formatToParts(startDateTime).find(part => part.type === 'timeZoneName')?.value || '';

        // Get full timezone name (e.g., Mountain Daylight Time)
        const timezoneName = new Intl.DateTimeFormat('en-US', {
          timeZone: creatorTimezone,
          timeZoneName: 'long'
        }).formatToParts(startDateTime).find(part => part.type === 'timeZoneName')?.value || creatorTimezone;

        // Format date with day of week (e.g., "Thursday, October 23, 2025")
        const dateOptions = {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: creatorTimezone
        };

        // Format time (e.g., "10:48 AM")
        const timeOptions = {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: creatorTimezone
        };

        const formattedDate = startDateTime.toLocaleDateString('en-US', dateOptions);
        const formattedTime = startDateTime.toLocaleTimeString('en-US', timeOptions);

        details += `<p style="margin: 8px 0; color: #555;">
          <strong>Date:</strong> ${formattedDate}<br/>
          <strong>Time:</strong> ${formattedTime} ${timezoneShort}
        </p>`;

        details += `<div style="margin: 10px 0; padding: 10px; background-color: #e3f2fd; border-radius: 4px; font-size: 13px; color: #555;">
          <strong>Timezone Information:</strong> This meeting is scheduled for <strong>${formattedTime} ${timezoneShort}</strong> (${timezoneName}).
          Recipients in different timezones will automatically see the meeting at the corresponding time in their timezone when they add the calendar invite (.ics file) to their calendar.
        </div>`;
      }
      
      if (duration) {
        const hours = Math.floor(duration / 60);
        const minutes = duration % 60;
        const durationText = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        details += `<p style="margin: 5px 0; color: #555;"><strong>Duration:</strong> ${durationText}</p>`;
      }
      
      if (recurrence && recurrence !== 'none') {
        const recurrenceText = recurrence.charAt(0).toUpperCase() + recurrence.slice(1);
        details += `<p style="margin: 5px 0; color: #555;"><strong>Recurrence:</strong> ${recurrenceText}</p>`;
      }
      
      if (notes) {
        details += `<p style="margin: 5px 0; color: #555;"><strong>Notes:</strong> ${notes}</p>`;
      }
      
      details += `</div>`;
      return details;
    };

    const emailContents = `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="x-apple-disable-message-reformatting">
    <title>Meeting Confirmation</title>
    <style type="text/css">
      /* Base Styles */
      body {
        margin: 0;
        padding: 0;
        -webkit-text-size-adjust: 100%;
        background-color: #e8f3fc;
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
        color: #333;
      }
      table {
        border-collapse: collapse;
      }
      a {
        color: #1a82e2;
        text-decoration: none;
      }
      /* Responsive Layout */
      @media only screen and (min-width: 520px) {
        .container {
          width: 500px !important;
        }
      }
      @media only screen and (max-width: 520px) {
        .container {
          width: 100% !important;
          padding: 0 10px;
        }
      }
    </style>
  </head>
  <body>
    <table width="100%" bgcolor="#e8f3fc" cellpadding="0" cellspacing="0">
      <tr>
        <td align="center">
          <!-- Container -->
          <table class="container" cellpadding="0" cellspacing="0" style="background-color: #ffffff; margin: 20px 0; border-radius: 8px; overflow: hidden; box-shadow: 0px 2px 6px rgba(0,0,0,0.15);">
            <!-- Header -->
            <tr>
              <td align="center" style="padding: 20px; background-color: #4caf50;">
                <h1 style="margin: 0; font-size: 24px; font-weight: 400; color: #ffffff;">Your CANmeet Meeting Has Been Created</h1>
              </td>
            </tr>
            <!-- Meeting Details -->
            <tr>
              <td align="center" style="padding: 20px;">
                <h2 style="margin: 0; margin-bottom: 15px; font-size: 20px; font-weight: 400; color: #333;">Meeting Confirmation</h2>
                <p style="margin: 0 0 15px 0; font-size: 16px; line-height: 1.4; color: #555;">
                  Your meeting has been successfully created. You can use the link below to join the meeting or share it with others.
                </p>
                ${formatMeetingDetails()}
              </td>
            </tr>
            <!-- Join Button -->
            <tr>
              <td align="center" style="padding: 20px;">
                <a href="${meetingUrl}" target="_blank" style="display: inline-block; background-color: #4caf50; color: #ffffff; padding: 12px 24px; border-radius: 4px; font-size: 16px; font-weight: 600; margin-right: 10px;">
                  Join Meeting
                </a>
              </td>
            </tr>
            <!-- Alternative Link -->
            <tr>
              <td align="center" style="padding: 10px 20px 20px; font-size: 14px; color: #666;">
                <p style="margin: 0;">Or copy and paste the following link into your browser:</p>
                <p style="margin: 5px 0 0;">
                  <a href="${meetingUrl}" target="_blank" style="word-break: break-all;">${meetingUrl}</a>
                </p>
              </td>
            </tr>
            <!-- Footer -->
            <tr>
              <td align="center" style="padding: 15px 20px; background-color: #f8f9fa; font-size: 12px; color: #666;">
                <p style="margin: 0;">This is a confirmation email for your CANmeet meeting. You can share this link with others to invite them to join.</p>
              </td>
            </tr>
          </table>
          <!-- End Container -->
        </td>
      </tr>
    </table>
  </body>
</html>`;

    const result = await SendCANmail(
      0,
      "meet@streamdata.com",
      "meet@streamdata.com",
      "Your CANmeet Meeting Confirmation",
      emailContents,
      null,
      userEmail
    );
    return result;
  } catch (error) {
    console.error("[sendMeetingConfirmation] Error sending meeting confirmation:", error);
    throw error;
  }
};
